import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash, ArrowUp, ArrowDown, DownloadSimple, Funnel } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, can } from "@/context/AuthContext";
import SeasonSelector from "@/components/SeasonSelector";

const CATEGORIAS = {
  ingreso: ["taquilla", "cantina", "rifas", "merchandising", "publicidad", "donaciones", "otros"],
  gasto: ["arbitros", "seguridad", "ambulancia", "limpieza", "personal", "comida", "bebida", "material", "otros"],
};

const fmtEUR = (n) => (Number(n) || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

export default function Transacciones({ tipo }) {
  const { user } = useAuth();
  const [txs, setTxs] = useState([]);
  const [matches, setMatches] = useState([]);
  const [activeMatch, setActiveMatch] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    categoria: CATEGORIAS[tipo][0], subcategoria: "", concepto: "",
    importe: 0, observaciones: "", es_general: false, temporada: "",
    fecha: "",
  });

  // Filtros
  const [filterMatch, setFilterMatch] = useState("active"); // 'active' | 'all' | 'general' | match_id
  const [filterCategoria, setFilterCategoria] = useState("");
  const [filterTemporada, setFilterTemporada] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  const canEdit = can(user, "admin", tipo === "ingreso" ? "taquilla" : null, "tesoreria");

  const loadMatches = useCallback(async () => {
    try {
      const [ms, active] = await Promise.all([
        api.get("/matches"),
        api.get("/matches/active"),
      ]);
      setMatches(ms.data);
      setActiveMatch(active.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const params = { tipo };
      if (filterMatch && filterMatch !== "all") params.match_id = filterMatch;
      if (filterCategoria) params.categoria = filterCategoria;
      if (filterTemporada) params.temporada = filterTemporada;
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      const { data } = await api.get("/transactions", { params });
      setTxs(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [tipo, filterMatch, filterCategoria, filterTemporada, fechaDesde, fechaHasta]);

  useEffect(() => {
    loadMatches();
    setForm({
      categoria: CATEGORIAS[tipo][0], subcategoria: "", concepto: "",
      importe: 0, observaciones: "", es_general: false, temporada: "", fecha: "",
    });
    setFilterCategoria("");
  }, [tipo, loadMatches]);

  useEffect(() => {
    load();
  }, [load]);

  const total = txs.reduce((sum, t) => sum + (t.importe || 0), 0);
  const byCat = useMemo(() => {
    return txs.reduce((acc, t) => {
      acc[t.categoria] = (acc[t.categoria] || 0) + (t.importe || 0);
      return acc;
    }, {});
  }, [txs]);

  // Match seleccionado (para saber si permitir registrar)
  const selectedMatchId = filterMatch === "active"
    ? activeMatch?.id
    : (filterMatch === "all" || filterMatch === "general") ? null : filterMatch;
  const isGeneralView = filterMatch === "general";
  // Permitir registrar si: hay match seleccionado O si estamos en vista general (=> gasto general)
  const canRegister = canEdit && (!!selectedMatchId || isGeneralView);

  // Temporadas disponibles derivadas de matches + txs
  const availableSeasons = useMemo(() => {
    const set = new Set();
    matches.forEach((m) => m.temporada && set.add(m.temporada));
    txs.forEach((t) => t.temporada && set.add(t.temporada));
    return Array.from(set).sort().reverse();
  }, [matches, txs]);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        tipo,
        categoria: form.categoria,
        subcategoria: form.subcategoria || null,
        concepto: form.concepto,
        importe: form.importe,
        observaciones: form.observaciones,
        es_general: isGeneralView || form.es_general,
      };
      if (form.fecha) payload.fecha = form.fecha;
      if (payload.es_general) {
        payload.match_id = null;
        payload.temporada = form.temporada || null;
      } else {
        payload.match_id = selectedMatchId;
      }
      await api.post("/transactions", payload);
      toast.success(tipo === "ingreso" ? "Ingreso registrado" : "Gasto registrado");
      setShowForm(false);
      setForm({
        categoria: CATEGORIAS[tipo][0], subcategoria: "", concepto: "",
        importe: 0, observaciones: "", es_general: false, temporada: "", fecha: "",
      });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este movimiento?")) return;
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const exportCSV = async () => {
    try {
      const params = { tipo };
      if (filterMatch && filterMatch !== "all") params.match_id = filterMatch === "active" ? (activeMatch?.id || "") : filterMatch;
      if (filterCategoria) params.categoria = filterCategoria;
      if (filterTemporada) params.temporada = filterTemporada;
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      const { data, headers } = await api.get("/transactions/export.csv", { params, responseType: "blob" });
      const blob = new Blob([data], { type: headers["content-type"] || "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const match = (headers["content-disposition"] || "").match(/filename="?([^";]+)"?/);
      a.download = match ? match[1] : `movimientos_${tipo}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al exportar");
    }
  };

  const exportPDF = async () => {
    try {
      const params = { tipo };
      if (filterMatch && filterMatch !== "all") params.match_id = filterMatch === "active" ? (activeMatch?.id || "") : filterMatch;
      if (filterCategoria) params.categoria = filterCategoria;
      if (filterTemporada) params.temporada = filterTemporada;
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      const { data, headers } = await api.get("/transactions/export.pdf", { params, responseType: "blob" });
      const blob = new Blob([data], { type: headers["content-type"] || "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const match = (headers["content-disposition"] || "").match(/filename="?([^";]+)"?/);
      a.download = match ? match[1] : `informe_${tipo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al exportar PDF");
    }
  };

  const clearFilters = () => {
    setFilterMatch("active");
    setFilterCategoria("");
    setFilterTemporada("");
    setFechaDesde("");
    setFechaHasta("");
  };

  const matchLabel = (m) => `vs ${m.rival} — ${m.fecha}${m.estado === "activo" ? " · ACTIVO" : ""}`;
  const currentMatch = filterMatch === "active" ? activeMatch : (filterMatch === "all" || filterMatch === "general" ? null : matches.find((m) => m.id === filterMatch));

  const headerContext = () => {
    if (filterMatch === "all") return "Todos los partidos";
    if (filterMatch === "general") return "Movimientos generales del club (sin partido)";
    if (currentMatch) return `Partido: vs ${currentMatch.rival} (${currentMatch.fecha})`;
    return "Sin partido seleccionado";
  };

  const ACCENT = tipo === "ingreso" ? "#22C55E" : "#EF4444";
  const Icon = tipo === "ingreso" ? ArrowUp : ArrowDown;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase font-bold" style={{ color: ACCENT }}>
            Módulo 7 — Economía
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tighter flex items-center gap-3">
            <Icon size={36} weight="bold" style={{ color: ACCENT }} />
            {tipo === "ingreso" ? "Ingresos" : "Gastos"}
          </h1>
          <div className="text-sm text-slate-500 mt-1">{headerContext()}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="export-csv-btn"
            onClick={exportCSV}
            className="px-4 h-11 border border-slate-300 hover:border-slate-900 hover:bg-slate-50 text-slate-800 flex items-center gap-2 font-display font-bold uppercase tracking-wide text-sm active:scale-[0.98]"
            style={{ borderRadius: 6 }}
          >
            <DownloadSimple size={16} weight="bold" /> Exportar CSV
          </button>
          <button
            data-testid="export-pdf-btn"
            onClick={exportPDF}
            className="px-4 h-11 border border-[#dc2626]/40 hover:border-[#dc2626] hover:bg-[#dc2626]/5 text-[#dc2626] flex items-center gap-2 font-display font-bold uppercase tracking-wide text-sm active:scale-[0.98]"
            style={{ borderRadius: 6 }}
          >
            <DownloadSimple size={16} weight="bold" /> Exportar PDF
          </button>
          {canRegister && (
            <button
              data-testid={`add-${tipo}-btn`}
              onClick={() => setShowForm(true)}
              className="px-5 h-11 flex items-center gap-2 font-display font-bold uppercase tracking-wide text-sm active:scale-[0.98] text-white shadow-md"
              style={{ borderRadius: 6, background: ACCENT }}
            >
              <Plus size={18} weight="bold" /> Registrar {tipo === "ingreso" ? "Ingreso" : "Gasto"}
            </button>
          )}
        </div>
      </div>

      {/* Filters bar */}
      <div className="bg-white border border-slate-200 p-4 flex flex-wrap items-end gap-3" style={{ borderRadius: 8 }}>
        <div className="flex items-center gap-2 text-slate-700 text-xs font-bold uppercase tracking-widest mr-2">
          <Funnel size={16} weight="bold" /> Filtros
        </div>
        <FieldSmall label="Partido">
          <select
            data-testid="filter-match"
            value={filterMatch}
            onChange={(e) => setFilterMatch(e.target.value)}
            className={inputSmall}
          >
            <option value="active">Partido activo</option>
            <option value="all">Todos los partidos</option>
            <option value="general">General (sin partido)</option>
            <optgroup label="Elegir partido">
              {matches.map((m) => (
                <option key={m.id} value={m.id}>{matchLabel(m)}</option>
              ))}
            </optgroup>
          </select>
        </FieldSmall>
        <FieldSmall label="Temporada">
          <SeasonSelector
            value={filterTemporada}
            onChange={setFilterTemporada}
            includeAll
            size="sm"
            testid="filter-temporada"
          />
        </FieldSmall>
        <FieldSmall label="Categoría">
          <select
            data-testid="filter-categoria"
            value={filterCategoria}
            onChange={(e) => setFilterCategoria(e.target.value)}
            className={inputSmall}
          >
            <option value="">Todas</option>
            {CATEGORIAS[tipo].map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
          </select>
        </FieldSmall>
        <FieldSmall label="Desde">
          <input data-testid="filter-desde" type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className={inputSmall} />
        </FieldSmall>
        <FieldSmall label="Hasta">
          <input data-testid="filter-hasta" type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className={inputSmall} />
        </FieldSmall>
        <button
          onClick={clearFilters}
          data-testid="filter-clear"
          className="h-9 px-3 border border-slate-200 hover:border-slate-500 text-xs font-bold uppercase tracking-widest text-slate-600"
          style={{ borderRadius: 6 }}
        >
          Limpiar
        </button>
      </div>

      {/* Total + by category */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 p-6" style={{ borderRadius: 6 }}>
          <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">Total {tipo}s</div>
          <div className="font-display font-bold text-5xl mt-2 tracking-tighter" style={{ color: ACCENT }} data-testid="tx-total">
            {fmtEUR(total)}
          </div>
          <div className="text-xs text-slate-500 mt-2">{txs.length} movimiento(s)</div>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 p-6" style={{ borderRadius: 6 }}>
          <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold mb-4">Desglose por categoría</div>
          <div className="space-y-2">
            {CATEGORIAS[tipo].map((cat) => {
              const v = byCat[cat] || 0;
              const pct = total ? (v / total) * 100 : 0;
              return (
                <div key={cat}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="capitalize text-slate-700">{cat}</span>
                    <span className="font-medium tabular-nums">{fmtEUR(v)}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100" style={{ borderRadius: 999 }}>
                    <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: ACCENT, borderRadius: 999 }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 overflow-x-auto" style={{ borderRadius: 6 }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-slate-500 font-bold">Fecha</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-slate-500 font-bold">Categoría</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-slate-500 font-bold">Concepto</th>
              {filterMatch === "all" && <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-slate-500 font-bold hidden lg:table-cell">Partido</th>}
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-slate-500 font-bold hidden md:table-cell">Usuario</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-slate-500 font-bold text-right">Importe</th>
              {canEdit && <th className="px-4 py-3"></th>}
            </tr>
          </thead>
          <tbody>
            {txs.length === 0 && (
              <tr><td colSpan={canEdit ? 7 : 6} className="text-center py-12 text-slate-500">Sin movimientos con los filtros actuales</td></tr>
            )}
            {txs.map((t) => {
              const m = matches.find((mm) => mm.id === t.match_id);
              return (
                <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`tx-row-${t.id}`}>
                  <td className="px-4 py-3 text-slate-600">{new Date(t.timestamp).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="px-4 py-3 capitalize">
                    {t.categoria}
                    {t.subcategoria && <div className="text-[10px] text-slate-500 normal-case">{t.subcategoria}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{t.concepto}</div>
                    {t.observaciones && <div className="text-xs text-slate-500">{t.observaciones}</div>}
                  </td>
                  {filterMatch === "all" && (
                    <td className="px-4 py-3 hidden lg:table-cell text-slate-600 text-xs">
                      {t.es_general || !t.match_id
                        ? <span className="uppercase tracking-widest text-[10px] font-bold text-[#2563EB]">General</span>
                        : m ? <>vs {m.rival} <span className="text-slate-400">({m.fecha})</span></> : "—"}
                    </td>
                  )}
                  <td className="px-4 py-3 hidden md:table-cell text-slate-600">{t.user_name}</td>
                  <td className="px-4 py-3 text-right font-display font-bold text-lg" style={{ color: ACCENT }}>
                    {fmtEUR(t.importe)}
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3 text-right">
                      <button data-testid={`tx-delete-${t.id}`} onClick={() => remove(t.id)} className="h-8 w-8 inline-flex items-center justify-center border border-slate-200 hover:border-[#EF4444] hover:text-[#EF4444]" style={{ borderRadius: 6 }}>
                        <Trash size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          {txs.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                <td className="px-4 py-3 uppercase text-[10px] tracking-widest text-slate-700" colSpan={filterMatch === "all" ? 5 : 4}>Totales</td>
                <td className="px-4 py-3 text-right font-display font-bold text-xl" style={{ color: ACCENT }} data-testid="tx-footer-total">{fmtEUR(total)}</td>
                {canEdit && <td></td>}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Modal registrar */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={onSubmit} className="bg-white border border-slate-200 w-full max-w-lg p-6 space-y-4" style={{ borderRadius: 6 }}>
            <h2 className="font-display font-bold text-2xl uppercase">
              Registrar {tipo === "ingreso" ? "Ingreso" : "Gasto"}
            </h2>

            {/* Toggle general vs partido */}
            {!isGeneralView && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  data-testid="form-es-general"
                  type="checkbox"
                  checked={form.es_general}
                  onChange={(e) => setForm({ ...form, es_general: e.target.checked })}
                  className="h-4 w-4 accent-[#2563EB]"
                />
                <span>
                  <strong>General del club</strong> (sin partido asociado)
                </span>
              </label>
            )}

            <p className="text-xs text-slate-500">
              {(isGeneralView || form.es_general)
                ? <>Este movimiento será <strong>general del club</strong> (sin partido).</>
                : <>Partido: <span className="font-bold text-slate-700">{currentMatch ? `vs ${currentMatch.rival} (${currentMatch.fecha})` : "—"}</span></>}
            </p>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Categoría" required>
                <select data-testid="tx-categoria" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className={inputCls}>
                  {CATEGORIAS[tipo].map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
                </select>
              </Field>
              <Field label="Subcategoría">
                <input
                  data-testid="tx-subcategoria"
                  value={form.subcategoria}
                  onChange={(e) => setForm({ ...form, subcategoria: e.target.value })}
                  className={inputCls}
                  placeholder="Opcional (ej. dietas, viaje)"
                />
              </Field>
              <Field label="Importe €" required>
                <input data-testid="tx-importe" type="number" step="0.01" required value={form.importe} onChange={(e) => setForm({ ...form, importe: parseFloat(e.target.value) || 0 })} className={inputCls} />
              </Field>
              <Field label="Fecha (opcional)">
                <input
                  data-testid="tx-fecha"
                  type="date"
                  value={form.fecha}
                  onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                  className={inputCls}
                />
              </Field>
              {(isGeneralView || form.es_general) && (
                <Field label="Temporada">
                  <input
                    data-testid="tx-temporada"
                    value={form.temporada}
                    onChange={(e) => setForm({ ...form, temporada: e.target.value })}
                    className={inputCls}
                    placeholder="Ej: 2026-27"
                  />
                </Field>
              )}
            </div>
            <Field label="Concepto" required>
              <input data-testid="tx-concepto" required value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Observaciones">
              <textarea rows={2} value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} className={inputCls} />
            </Field>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-5 h-11 border border-slate-200" style={{ borderRadius: 6 }}>Cancelar</button>
              <button data-testid="tx-submit" type="submit" className="px-5 h-11 font-display font-bold uppercase tracking-wide text-white" style={{ borderRadius: 6, background: ACCENT }}>Guardar</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-slate-50 border border-slate-200 px-3 h-10 text-slate-900 focus:border-[#2563EB] outline-none placeholder:text-slate-400";
const inputSmall = "bg-white border border-slate-200 px-2 h-9 text-slate-900 focus:border-[#2563EB] outline-none text-sm min-w-[140px]";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.2em] uppercase font-bold text-slate-600 mb-1.5">
        {label}{required && <span className="text-[#EF4444]"> *</span>}
      </span>
      {children}
    </label>
  );
}

function FieldSmall({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[9px] tracking-widest uppercase font-bold text-slate-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
